import {HttpClient} from '@angular/common/http';
import {Inject, Injectable, Optional} from '@angular/core';
import * as _ from 'lodash-es';
import {defer, Observable, of, throwError} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {fetchAllPages, handleResponseError} from '../../common/api-helpers';
import {createPendingRequest, PendingRequest} from '../../common/request-synchronization';
import {AccountType} from '../../enums/account-type';
import {
    Exchange,
    ExchangeAllowedInAccount,
    EXCHANGES_ALLOWED_IN_ACCOUNT,
    isExchangeWithPrivateVwapMdOnly
} from '../../enums/exchange';
import {QueryLangFilterOperator} from '../../enums/query-lang-filter-operator';
import {SortDirection} from '../../enums/sort-direction';
import {ExchangeAccount} from '../../models/exchange-account';
import {CommonListFilter} from '../../models/filters/common-list-filter';
import {FilterCriterion} from '../../models/filters/filter-criterion';
import {QueryLangFilterCriterion} from '../../models/filters/query-lang-filter-criterion';
import {Security} from '../../models/security';
import {formatSecurityKey, SecurityKey} from '../../models/security-key';
import {AppConfigService} from '../../services/config.service';
import {UserService} from '../../services/user.service';
import {AccountService} from '../accounts';
import {SecurityService} from '../security';
import {CUSTOM_SECURITY_REQUEST_HANDLERS, CustomSecurityRequestHandler} from '../security/custom-security-request';
import {
    SECURITY_CURRENCY_FILTER_FIELD_NAMES,
    SECURITY_EXCHANGE_FILTER_FIELD_NAMES,
    SECURITY_SYMBOL_FILTER_FIELD_NAMES
} from '../security/security-filter-criterion-utils';
import {SecurityStreamingService} from '../security/security-streaming';
import {isSmartSecurity, isSmartSecurityKey, SmartSecurity} from './smart-security';

@Injectable()
export class SmartSecurityService extends SecurityService {

    // TODO: [HED-418] Review for BO
    private readonly accountUrl: string = this.appConfigService.api['account'];

    private readonly smartSecurityResolutionsDict: {[securityKeyStr: string]: Security[]} = {};

    private pendingSmartAccountExchangesRequest: PendingRequest<Exchange[]>;

    private smartAccountExchanges: Exchange[];

    private readonly isSmartSecurityEnabled = !!this.appConfigService.appConfig.isSmartSecurityEnabled;

    constructor(
        protected override httpClient: HttpClient,
        protected override appConfigService: AppConfigService,

        @Optional() // It's not required in case of disabled smart securities.
        protected accountService: AccountService,

        private userService: UserService,

        @Optional()
        protected override securityStreamingService?: SecurityStreamingService,

        @Optional()
        @Inject(CUSTOM_SECURITY_REQUEST_HANDLERS)
        protected override customSecurityRequestHandlers?: CustomSecurityRequestHandler[]
    ) {
        super(httpClient, appConfigService, securityStreamingService, customSecurityRequestHandlers);

        this.listenToAccounts();
    }

    public override cacheSecurity(security: Security): void {

        // Sometimes API sends trimmed object for Smart Security (e.g. in Quotes streaming).
        const isTrimmedSmartSecurity = isSmartSecurityKey(security)
            && (!security.quoteAsset || !security.baseAsset);

        if (!isTrimmedSmartSecurity) {
            super.cacheSecurity(security);
        }
    }

    public getAccountsExchanges(): Exchange[] {
        return _.uniq(
            this.accountService.getAllAccountsFromCache()
                .filter(AccountService.isNotCustomAccount)
                .map(account => account.exchange)
                .filter(exchange => !isExchangeWithPrivateVwapMdOnly(exchange))
        );
    }

    public createSmartSecurity(smartSecurityKey: SecurityKey, boundSecurities: Security[]): SmartSecurity {
        boundSecurities = boundSecurities && _.sortBy(
            boundSecurities,
            security => formatSecurityKey(security)
        );

        const mapToProperty = <T, K extends keyof T>(collection: T[], propertyName: K) =>
            collection.map(security => security[propertyName]);

        const max = (values: number[]) => values.every(_.isFinite)
            ? Math.max(...values)
            : null;

        const min = (values: number[]) => values.every(_.isFinite)
            ? Math.min(...values)
            : null;

        const smartSecurity: SmartSecurity = boundSecurities.length
            ? {
                ...boundSecurities[0],
                id:               null,
                securityExchange: Exchange.SMART,
                priceScale:       max(mapToProperty(boundSecurities, 'priceScale')),
                priceViewScale:   max(mapToProperty(boundSecurities, 'priceViewScale')),
                qtyScale:         max(mapToProperty(boundSecurities, 'qtyScale')),
                qtyViewScale:     max(mapToProperty(boundSecurities, 'qtyViewScale')),
                reportScale:      max(mapToProperty(boundSecurities, 'reportScale')),
                reportViewScale:  max(mapToProperty(boundSecurities, 'reportViewScale')),
                priceStep:        min(mapToProperty(boundSecurities, 'priceStep')),
                qtyStep:          min(mapToProperty(boundSecurities, 'qtyStep')),
                minNotional:      min(mapToProperty(boundSecurities, 'minNotional')),
                minPrice:         min(mapToProperty(boundSecurities, 'minPrice')),
                minQty:           min(mapToProperty(boundSecurities, 'minQty')),
                maxPrice:         max(mapToProperty(boundSecurities, 'maxPrice')),
                maxQty:           max(mapToProperty(boundSecurities, 'maxQty'))
            }
            : {
                ...smartSecurityKey,
                id:               null,
                securityExchange: Exchange.SMART
            } as SmartSecurity;

        const smartSecurityKeyStr = formatSecurityKey(smartSecurityKey);

        this.smartSecurityResolutionsDict[smartSecurityKeyStr] = boundSecurities;
        this.cacheSecurity(smartSecurity);

        return smartSecurity;
    }

    public getOrCreateSmartSecurity(securityKey: SecurityKey, boundSecurities: Security[]): SmartSecurity {
        const smartSecurityKey: SecurityKey = {
            symbol: securityKey.symbol,
            securityExchange: Exchange.SMART,
            currency: securityKey.currency
        };

        return this.getSecurityFromCacheBySecurityKey(smartSecurityKey) as SmartSecurity
            || this.createSmartSecurity(smartSecurityKey, boundSecurities);
    }

    public resolveSmartSecurities(smartSecurityKeys: SecurityKey[]): Observable<Security[][]> {
        const smartSecurityResolutions: Security[][] = [];
        const isResolutionsMissing = smartSecurityKeys.some(smartSecurityKey => {
            const resolution = this.smartSecurityResolutionsDict[formatSecurityKey(smartSecurityKey)];

            smartSecurityResolutions.push(resolution);

            return !resolution;
        });

        return isResolutionsMissing
            ? this.createSmartSecuritiesResolutionRequest(smartSecurityKeys)
            : of(smartSecurityResolutions);
    }

    public resolveSmartSecurity(smartSecurityKey: SecurityKey): Observable<Security[]> {
        return this.resolveSmartSecurities([smartSecurityKey]).pipe(
            map(securitiesGroups => securitiesGroups[0])
        );
    }

    public getSmartSecurityAccountCreationExchange(smartSecurityKey: SecurityKey): Observable<Exchange> {
        return this.getSmartSecurityRealExchanges(smartSecurityKey).pipe(
            map(exchanges => exchanges
                .filter(exchange =>
                    EXCHANGES_ALLOWED_IN_ACCOUNT.includes(exchange as ExchangeAllowedInAccount)
                )[0]
            )
        );
    }

    public getSmartAccountExchanges(): Observable<Exchange[]> {
        const fetch = () => {
            const pendingRequest = this.pendingSmartAccountExchangesRequest
                || createPendingRequest(
                    defer(() => this.createSmartAccountExchangesRequest()),
                    request => this.pendingSmartAccountExchangesRequest = request,
                    () => this.pendingSmartAccountExchangesRequest = undefined
                );

            return pendingRequest.request$.pipe(
                tap(exchanges => this.smartAccountExchanges = exchanges)
            );
        };

        return this.smartAccountExchanges
            ? of(this.smartAccountExchanges)
            : fetch();
    }

    /**
     * Returns list of simple accounts allowed in Smart
     */
    public getSmartBoundExistingAccounts(): Observable<ExchangeAccount[]> {
        return this.getSmartAccountExchanges().pipe(
            map(exchanges =>
                this.accountService.getAllAccountsFromCache()
                    .filter(AccountService.isNotCustomAccount)
                    .filter(account => exchanges.includes(account.exchange))

                    // [HG-222] Exclude private VWAP MD exchanges from Smart
                    .filter(account => !isExchangeWithPrivateVwapMdOnly(account.exchange))
            )
        );
    }

    /**
     * Returns list of exchanges filtered by Smart account and by existing simple accounts
     */
    public getSmartBoundExistingAccountsExchanges(): Observable<Exchange[]> {
        return this.getSmartBoundExistingAccounts().pipe(
            map(accounts => _.uniq(accounts.map(account => account.exchange)))
        );
    }

    public getSecurityUserAccounts(securityKey: SecurityKey): Observable<ExchangeAccount[]> {
        const getAllowedAccounts = (security: Security): ExchangeAccount[] => {
            const accounts = this.accountService.getAccountsByExchangeFromCache(security.securityExchange);

            return security.marginEnabled
                ? accounts
                : accounts.filter(account => account.type !== AccountType.MARGIN);
        };

        return isSmartSecurityKey(securityKey)
            ? this.getSmartBoundExistingAccounts()
            : this.getSecurityBySecurityKey(securityKey).pipe(
                map(getAllowedAccounts)
            );
    }

    protected override createSecurityRequestBySecurityKey(securityKey: SecurityKey): Observable<Security> {

        /**
         * Passing this constant to {@link throwError} will be converted to empty response.
         */
        const ERROR_NO_ACCOUNTS_BOUND_TO_SMART = {};

        let request$: Observable<Security>;
        const isSmartSecurityRequest = isSmartSecurityKey(securityKey);

        if (isSmartSecurityRequest && !this.getAccountsExchanges().length) {
            request$ = of(null);
        } else {
            const exchangeFilterCriterion$: Observable<QueryLangFilterCriterion> = !isSmartSecurityRequest
                ? of(
                    new QueryLangFilterCriterion({
                        fieldNames: SECURITY_EXCHANGE_FILTER_FIELD_NAMES,
                        operator:   QueryLangFilterOperator.EQ,
                        query:      securityKey.securityExchange
                    })
                )
                : this.getSmartBoundExistingAccountsExchanges().pipe(
                    switchMap(exchanges =>
                        exchanges.length
                            ? of(
                                new QueryLangFilterCriterion({
                                    fieldNames: SECURITY_EXCHANGE_FILTER_FIELD_NAMES,
                                    operator:   QueryLangFilterOperator.IN,
                                    query:      exchanges.join(',')
                                })
                            )
                            : throwError(ERROR_NO_ACCOUNTS_BOUND_TO_SMART)
                    )
                );

            const securities$: Observable<Security[]> = exchangeFilterCriterion$.pipe(
                switchMap(exchangeFilterCriterion => {
                    const filterCriteria: FilterCriterion[] = [
                        new QueryLangFilterCriterion({
                            fieldNames: SECURITY_SYMBOL_FILTER_FIELD_NAMES,
                            operator:   QueryLangFilterOperator.EQ,
                            query:      securityKey.symbol
                        }),
                        exchangeFilterCriterion,
                        new QueryLangFilterCriterion({
                            fieldNames: SECURITY_CURRENCY_FILTER_FIELD_NAMES,
                            operator:   QueryLangFilterOperator.EQ,
                            query:      securityKey.currency
                        })
                    ];

                    const listFilter: CommonListFilter = {
                        filters:       filterCriteria,
                        pageIndex:     0,
                        pageSize:      isSmartSecurityRequest ? 100 : 1,
                        sortColumn:    null,
                        sortDirection: null
                    };

                    return isSmartSecurityRequest
                        ? fetchAllPages(
                            currentPageFilter => this.getSecurities(currentPageFilter),
                            listFilter
                        )
                        : this.getSecurities(listFilter).pipe(
                            map(listResponse => listResponse.data)
                        );
                })
            );

            request$ = securities$.pipe(
                map(securities => {
                    let security: Security = securities[0];

                    if (securities.length && isSmartSecurityRequest) {
                        security = this.createSmartSecurity(securityKey, securities);

                        this.cacheSecurity(security);
                    }

                    return security;
                }),
                catchError(error =>
                    error === ERROR_NO_ACCOUNTS_BOUND_TO_SMART
                        ? of(undefined)
                        : throwError(error)
                )
            );
        }

        return request$;
    }

    private createSmartSecuritiesResolutionRequest(smartSecurityKeys: SecurityKey[]): Observable<Security[][]> {
        let stream$: Observable<Security[][]>;

        const doResolve = (accounts: ExchangeAccount[]): Observable<Security[][]> => {
            smartSecurityKeys = _.uniqBy(smartSecurityKeys, smartSecurityKey => smartSecurityKey.symbol);

            const filterCriteria: FilterCriterion[] = [
                new QueryLangFilterCriterion({
                    fieldNames: SECURITY_SYMBOL_FILTER_FIELD_NAMES,
                    operator:   QueryLangFilterOperator.IN,
                    query:      smartSecurityKeys.map(securityKey => securityKey.symbol).join(',')
                }),
                new QueryLangFilterCriterion({
                    fieldNames: SECURITY_EXCHANGE_FILTER_FIELD_NAMES,
                    operator:   QueryLangFilterOperator.IN,
                    query:      accounts.map(account => account.exchange).join(',')
                })
            ];

            const listFilter: CommonListFilter = {
                filters:       filterCriteria,
                pageIndex:     0,
                pageSize:      100,
                sortColumn:    null,
                sortDirection: null
            };

            return fetchAllPages(
                currentPageFilter => this.getSecurities(currentPageFilter),
                listFilter
            ).pipe(
                map(allSecurities => {
                    const allSecuritiesGroupedBySymbol = _.groupBy(
                        allSecurities,
                        (security: Security) => security.symbol
                    );

                    return smartSecurityKeys
                        .map(smartSecurityKey => {
                            const boundSecurities = allSecuritiesGroupedBySymbol[smartSecurityKey.symbol] || [];

                            this.createSmartSecurity(smartSecurityKey, boundSecurities);

                            return boundSecurities;
                        });
                })
            );
        };

        if (!smartSecurityKeys.length) {
            stream$ = of([]);
        } else {
            stream$ = this.getSmartBoundExistingAccounts().pipe(
                switchMap(accounts =>
                    !accounts.length
                        ? of([])
                        : doResolve(accounts)
                )
            );
        }

        return stream$;
    }

    private getSmartSecurityRealExchanges(
        smartSecurityKey: SecurityKey,
        isOneEnough: boolean = false
    ): Observable<Exchange[]> {
        return this.getSmartAccountExchanges().pipe(
            switchMap(exchanges => {
                const filterCriterions: FilterCriterion[] = [
                    new QueryLangFilterCriterion({
                        fieldNames: SECURITY_SYMBOL_FILTER_FIELD_NAMES,
                        operator:   QueryLangFilterOperator.EQ,
                        query:      smartSecurityKey.symbol
                    }),
                    new QueryLangFilterCriterion({
                        fieldNames: SECURITY_CURRENCY_FILTER_FIELD_NAMES,
                        operator:   QueryLangFilterOperator.EQ,
                        query:      smartSecurityKey.currency
                    }),
                    new QueryLangFilterCriterion({
                        fieldNames: SECURITY_EXCHANGE_FILTER_FIELD_NAMES,
                        operator:   QueryLangFilterOperator.IN,
                        query:      exchanges.join(',')
                    })
                ];

                const listFilter: CommonListFilter = {
                    filters:       filterCriterions,
                    pageIndex:     0,
                    pageSize:      isOneEnough ? 1 : 100,
                    sortColumn:    SECURITY_EXCHANGE_FILTER_FIELD_NAMES[0],
                    sortDirection: SortDirection.ASC
                };

                return (
                    isOneEnough
                        ? this.getSecurities(listFilter).pipe(
                            map(listResponse => listResponse.data)
                        )
                        : fetchAllPages(
                            currentPageFilter => this.getSecurities(currentPageFilter),
                            listFilter
                        )
                ).pipe(
                    map(securities => securities
                        .map(security => security.securityExchange)
                        .filter(exchange => exchange !== Exchange.SMART)
                        .sort()
                    )
                );
            })
        );
    }

    private listenToAccounts(): void {
        if (this.isSmartSecurityEnabled) {
            this.accountService.accounts$.pipe(
                tap(() => {
                    this.clearSmartSecuritiesCache();
                    this.smartAccountExchanges = undefined;
                })
            ).subscribe();
        }
    }

    private clearSmartSecuritiesCache(): void {
        Object.keys(this.smartSecurityResolutionsDict).forEach(securityKeyStr =>
            delete this.smartSecurityResolutionsDict[securityKeyStr]
        );

        Object.keys(this.securitiesDict).forEach(securityKeyStr => {
            const security = this.securitiesDict[securityKeyStr];

            if (isSmartSecurity(security)) {
                delete this.securitiesDict[securityKeyStr];

                this.invalidateSecurityBySecurityKey(security);
            }
        });
    }

    private createSmartAccountExchangesRequest(): Observable<Exchange[]> {
        return this.isSmartSecurityEnabled && this.userService.user
            ? this.httpClient.get<Exchange[]>(`${this.accountUrl}/smart-exchanges`).pipe(
                catchError(handleResponseError),
                catchError(() => of([])) // May happen, if user has not passed Onboarding (no company).
            )
            : of<Exchange[]>([]);
    }

}
